import React, { useState } from 'react';
import {X} from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const images = [
    {
      url: "https://images.pexels.com/photos/13065690/pexels-photo-13065690.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Professional mechanic performing engine diagnostics",
      category: "Engine Work"
    },
    {
      url: "https://images.pexels.com/photos/5835264/pexels-photo-5835264.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Mechanic inspecting vehicle brakes",
      category: "Brake Service"
    },
    {
      url: "https://images.pexels.com/photos/4489737/pexels-photo-4489737.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Using advanced diagnostic equipment",
      category: "Diagnostics"
    },
    {
      url: "https://images.pexels.com/photos/3806288/pexels-photo-3806288.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Oil change and fluid service",
      category: "Maintenance"
    },
    {
      url: "https://images.pexels.com/photos/4315570/pexels-photo-4315570.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Tire inspection and replacement",
      category: "Tire Service"
    },
    {
      url: "https://images.pexels.com/photos/4489685/pexels-photo-4489685.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Battery testing and replacement",
      category: "Electrical"
    },
    {
      url: "https://images.pexels.com/photos/3806290/pexels-photo-3806290.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Suspension system repair",
      category: "Suspension"
    },
    {
      url: "https://images.pexels.com/photos/5835014/pexels-photo-5835014.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Mobile mechanic service on location",
      category: "Mobile Service"
    },
    {
      url: "https://images.pexels.com/photos/3964736/pexels-photo-3964736.jpeg?auto=compress&cs=tinysrgb&w=800",
      alt: "Professional tools and equipment",
      category: "Equipment"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#333333] to-[#4682B4] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Work <span className="text-[#FFD700]">Gallery</span>
          </h1>
          <p className="text-xl text-[#E0E0E0] max-w-3xl mx-auto leading-relaxed">
            See the quality and professionalism of our mobile mechanic services
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all"
                onClick={() => setSelectedImage(image.url)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#333333] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-semibold text-lg">{image.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#FFD700] transition-colors"
            onClick={() => setSelectedImage(null)}
            aria-label="Close gallery lightbox"
          >
            <X className="w-10 h-10" />
          </button>
          
          <img
            src={selectedImage}
            alt="Gallery image in full view"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Services Overview */}
      <section className="py-16 bg-[#E0E0E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
              Professional Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From routine maintenance to complex repairs, we handle it all at your location
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "Engine Diagnostics",
              "Brake Service",
              "Oil Changes",
              "Tire Service",
              "Battery Replacement",
              "Suspension Repair",
              "Electrical Work",
              "General Maintenance"
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white p-4 rounded-lg shadow text-center hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <p className="font-semibold text-[#333333]">{service}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#4682B4] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Our Services?
          </h2>
          <p className="text-xl mb-8 text-[#E0E0E0]">
            Contact us today for professional mobile mechanic services
          </p>
          
          <a
            href="tel:07454922145"
            className="bg-[#FFD700] text-[#333333] px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-all inline-block shadow-lg"
            aria-label="Call ONPOINT Mobile Mechanics for service"
          >
            Call 07454 922145
          </a>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
